/**
 * Product-neutral wire-shape types shared across product boundaries.
 *
 * ATTEST-DEPLOY-UNIT-1 R2 prep. Two independent reaches into product-owned
 * files landed here, both for the same reason: a type declaration a second
 * product genuinely needs, sitting in a file that also holds the owning
 * product's runtime logic.
 *
 * 1. `client.ts` holds `apiClient`, the axios instance every product's
 *    api/*.ts module calls through — it is the one file every product's
 *    build necessarily includes. It imported Deal/EvidenceEntry/
 *    FinancialYear from `@/types/deal` and FlexibleLineItem/Treatment from
 *    `@/lib/flexibleLineItems` — both QoE-owned paths — as `import type`,
 *    erased at build. No bundle ever actually carried QoE code into another
 *    product's build, but the coupling still read as real.
 * 2. Attest's `SigningIdentityCard` (what a letter will be signed as) reads
 *    the same firm profile the QoE-side Firm Profile page edits — a
 *    genuinely cross-product concept, not a QoE-only one — and imported
 *    `FirmProfile`/`FirmProfileResponse` from `@/pages/firmProfile/model`.
 *
 * This file holds ONLY the type declarations the second product needs: pure
 * shapes, no logic. `types/deal.ts`, `lib/flexibleLineItems.ts` and
 * `pages/firmProfile/model.ts` re-export from here so every existing import
 * of those paths keeps working unchanged. Owning-product runtime logic —
 * `buyerStatusView`, `routeFlexibleItems`, `normalizeTreatment`, `yearOf`,
 * `TREATMENT_MEANINGS`, `toForm`, `extractErrors`, and everything else —
 * stays exactly where it was; none of it moved. `TREATMENTS` is the one
 * non-interface export here, included only because `Treatment` is derived
 * from it (`(typeof TREATMENTS)[number]`) and client.ts needs `Treatment`;
 * the array itself carries no behavior.
 */

export type DealStatus =
  | 'setup'
  | 'seller_intake'
  | 'in_analysis'
  | 'report_ready'
  | 'delivered'
  | 'closed'
  | 'withdrawn'

// Canonical backend vocabulary — matches core/db/deal_service.create_deal and
// the buy_side/sell_side comparisons in BuyerPortal/SellerPortal. Do not use
// short forms ('buy'/'sell'); the API has no representation for them.
export type EngagementType = 'buy_side' | 'sell_side'

export interface Deal {
  deal_id: string
  business_name: string
  engagement_type: EngagementType
  status: DealStatus
  analyst: string | null
  firm: string
  created_at: string
  seller_token: string | null
  buyer_token: string | null
  // CURRENCY-1: ISO-4217 code every figure on this deal is displayed in.
  // Optional on the type because deals created before the column existed
  // come back without it; the UI falls back to USD.
  currency?: string
  loi_price: number | null
  docs_received: number
  docs_total: number
  // ENGAGE-SIZING-1: the industry captured on the New Engagement form, one of
  // lib/verticals.ts VERTICALS. QOE-CPA-FIXPACK-1 D5 makes this the single
  // source of truth for industry — the scope wizard prefills its complexity
  // weight from it instead of asking a second time.
  vertical?: string | null
  scope_tier?: "lite" | "focused" | "full"
  scope_score?: number | null
  scope_override?: boolean
  scope_override_note?: string | null
}

export interface FinancialYear {
  year: number
  revenue: number | null
  cogs: number | null
  gross_profit: number | null
  operating_expenses: number | null
  net_income: number | null
  ebitda: number | null
  depreciation: number | null
  amortization: number | null
  interest: number | null
  tax: number | null
}

export interface EvidenceEntry {
  source_document: string
  source_field: string | null
  source_value: number | null
  calculation: string | null
  calculated_value: number | null
  threshold_used: string | null
  threshold_value: number | null
  result: string
  confidence: string
}

export const TREATMENTS = [
  'revenue',
  'opex',
  'add_back',
  'owner_benefit',
  'debt_like',
  'memo',
] as const

export type Treatment = (typeof TREATMENTS)[number]

export type FlexibleItemSource = 'manual' | 'extracted'

/** A custom line item as the API returns it. */
export interface FlexibleLineItem {
  item_id?: string | null
  label?: string | null
  amount?: number | string | null
  year?: number | string | null
  /** null while an extracted line is still awaiting classification. */
  treatment?: string | null
  source?: FlexibleItemSource | string | null
  provenance?: Record<string, unknown> | null
  note?: string | null
}

export interface FirmProfile {
  firm_profile_id: string
  firm_id: string
  firm_legal_name: string | null
  firm_dba: string | null
  firm_website: string | null
  cpa_name: string | null
  cpa_license_no: string | null
  cpa_license_state: string | null
  firm_address_street: string | null
  firm_address_city: string | null
  firm_address_state: string | null
  firm_address_zip: string | null
  firm_logo_ref: string | null
  engagement_code_prefix: string | null
  disclaimer_boilerplate: string | null
  profile_status: string
  updated_by: string | null
  updated_at: string | null
}

export interface FirmProfileResponse {
  firm_id: string
  profile: FirmProfile | null
  needs_onboarding: boolean
  license_states: string[]
}
